import {formatNumber, formatPercentage} from "@/adapters/push.adapter";
import {BaseButton} from "@/components/BaseButton";
import {BaseCard} from "@/components/BaseCard";
import {PATHS} from "@/constants/routes";
import {usePushDashboard} from "@/hooks/usePush";
import {AlertTriangle, Bell, Check, History, Plus, Send, User} from "lucide-react";
import {useNavigate} from "react-router";
import {RecentCampaigns} from "./components/RecentCampaigns";
import {StatsCard} from "./components/StatsCard";

export const PushDashboard = () => {
  const {data: stats, isLoading} = usePushDashboard();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div
          className="text-sm"
          style={{color: "var(--color-text-secondary)"}}
        >
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex flex-col gap-6">
        <div
          className="text-sm"
          style={{color: "var(--color-danger)"}}
        >
          Failed to load dashboard data
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between">
        <div>
          <h1
            className="text-2xl font-bold"
            style={{color: "var(--color-text)"}}
          >
            Push Notifications Dashboard
          </h1>
          <p
            className="text-sm mt-1"
            style={{color: "var(--color-text-secondary)"}}
          >
            Manage and monitor push notification campaigns
          </p>
        </div>
        <div className="flex gap-2">
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={() => navigate(PATHS.PUSH_SEND_TO_USER)}
            leftIcon={<User size={16} />}
          >
            Send to User
          </BaseButton>
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={() => navigate(PATHS.PUSH_CAMPAIGNS)}
            leftIcon={<History size={16} />}
          >
            Campaign History
          </BaseButton>
          <BaseButton
            variant="primary"
            size="sm"
            onClick={() => navigate(PATHS.PUSH_CAMPAIGN_CREATE)}
            leftIcon={<Plus size={16} />}
          >
            Create Campaign
          </BaseButton>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatsCard
          label="Campaigns Sent"
          value={formatNumber(stats.totalCampaigns)}
          icon={<Send size={20} />}
          iconColor="var(--color-primary)"
        />
        <StatsCard
          label="Total Delivered"
          value={formatNumber(stats.totalDelivered)}
          icon={<Bell size={20} />}
          iconColor="var(--color-success)"
        />
        <StatsCard
          label="Avg Read Rate"
          value={formatPercentage(stats.avgReadRate)}
          icon={<Check size={20} />}
          iconColor="var(--color-accent)"
        />
        <StatsCard
          label="Total Failed"
          value={formatNumber(stats.totalFailed)}
          icon={<AlertTriangle size={20} />}
          iconColor="var(--color-danger)"
        />
      </div>

      {/* Recent Campaigns */}
      {stats.recentCampaigns && stats.recentCampaigns.length > 0 ? (
        <BaseCard padding="md">
          <div className="flex items-center justify-between mb-4">
            <h2
              className="text-base font-semibold"
              style={{color: "var(--color-text)"}}
            >
              Recent Campaigns
            </h2>
            <BaseButton
              variant="ghost"
              size="sm"
              onClick={() => navigate(PATHS.PUSH_CAMPAIGNS)}
              rightIcon={<History size={14} />}
            >
              View All
            </BaseButton>
          </div>
          <RecentCampaigns campaigns={stats.recentCampaigns} />
        </BaseCard>
      ) : (
        <BaseCard padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{backgroundColor: "var(--color-surface-2)"}}
            >
              <Bell
                size={32}
                style={{color: "var(--color-text-secondary)"}}
              />
            </div>
            <h3
              className="text-base font-semibold mb-2"
              style={{color: "var(--color-text)"}}
            >
              No campaigns yet
            </h3>
            <p
              className="text-sm mb-6 max-w-md"
              style={{color: "var(--color-text-secondary)"}}
            >
              Create your first push notification campaign to engage your users and keep them informed.
            </p>
            <div className="flex gap-3">
              <BaseButton
                variant="primary"
                size="md"
                onClick={() => navigate(PATHS.PUSH_CAMPAIGN_CREATE)}
                leftIcon={<Plus size={18} />}
              >
                Create First Campaign
              </BaseButton>
              <BaseButton
                variant="secondary"
                size="md"
                onClick={() => navigate(PATHS.PUSH_SEND_TO_USER)}
                leftIcon={<User size={18} />}
              >
                Send to User
              </BaseButton>
            </div>
          </div>
        </BaseCard>
      )}
    </div>
  );
};
